import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Baby, Activity, AlertCircle, ChevronDown, ChevronUp, Info, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

/**
 * PerinatalDilCard — fetches /api/perinatal-dil/ceiling and renders the
 * personalised DIL ceiling for a pregnant / lactating / postpartum user,
 * including auto-detected chronic conditions and the rationale behind
 * each modifier.
 *
 * Renders nothing when ``mode !== "perinatal"`` unless ``showWhenInactive``
 * is set (lets parent decide e.g. on Profile preview).
 */
export default function PerinatalDilCard({
  showWhenInactive = false,
  compact = false,
  className = "",
}) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchCeiling = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/perinatal-dil/ceiling`);
      setData(data);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchCeiling(); }, [fetchCeiling]);

  if (loading && !data) {
    return (
      <div className={`p-4 rounded-xl text-center ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin mx-auto opacity-50" />
      </div>
    );
  }
  if (!data) return null;

  const active = data.mode === "perinatal";

  // Hide entirely when not in perinatal window and parent didn't ask to show
  if (!active && !showWhenInactive) return null;

  // Non-perinatal placeholder (Profile preview etc.)
  if (!active && showWhenInactive) {
    return (
      <div
        className={`p-4 rounded-xl border-l-4 border-amber-400 ${
          isDark ? "bg-amber-500/10" : "bg-amber-50"
        } ${className}`}
        data-testid="perinatal-dil-card-inactive"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-amber-500" />
          <div className="text-xs leading-relaxed opacity-80">
            {data.disclaimer ||
              (bg
                ? "Не сте в перинатален период — стандартен DIL ceiling е активен."
                : "You are not in a perinatal window — standard DIL ceiling applies.")}
          </div>
        </div>
      </div>
    );
  }

  const stageLabel = bg ? data.label_bg : data.label_en;
  const conditions = data.conditions_applied || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl p-4 ${
        isDark
          ? "bg-gradient-to-br from-pink-500/15 via-purple-500/10 to-sky-500/15 border border-pink-500/30"
          : "bg-gradient-to-br from-pink-50 via-purple-50 to-sky-50 border border-pink-200"
      } ${className}`}
      data-testid="perinatal-dil-card"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isDark ? "bg-pink-500/30" : "bg-pink-200"
        }`}>
          <Baby className={`w-5 h-5 ${isDark ? "text-pink-200" : "text-pink-700"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">
            {bg ? "Перинатален DIL таван" : "Perinatal DIL ceiling"}
          </div>
          <div className="text-sm font-bold mt-0.5" data-testid="perinatal-stage-label">
            {stageLabel}
          </div>
        </div>
      </div>

      {/* iter559 — Weight-missing warning: ceiling е изчислен с default тегло */}
      {data.weight_missing && (
        <div
          className={`mt-3 p-2.5 rounded-lg flex items-start gap-2 ${
            isDark ? "bg-amber-500/15 border border-amber-500/30" : "bg-amber-50 border border-amber-200"
          }`}
          data-testid="perinatal-weight-missing-warning"
        >
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-[11px] leading-relaxed">
            {bg
              ? "DIL таванът е изчислен с приблизително тегло 60 kg. Въведете действителното си тегло в Профил → Тяло за по-точна стойност."
              : "DIL ceiling computed with approximate weight 60 kg. Enter your actual weight in Profile → Body for accurate value."}
          </div>
        </div>
      )}

      {/* Big number row */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <CeilingStat
          label={bg ? "DIL таван" : "DIL ceiling"}
          value={`${data.ceiling_g} g`}
          color="#EC4899"
          testid="perinatal-ceiling-g"
        />
        <CeilingStat
          label={bg ? "Въглехидрати" : "Carbs"}
          value={`${data.carb_target_g} g`}
          color="#F59E0B"
        />
        <CeilingStat
          label={bg ? "Протеин" : "Protein"}
          value={`${data.protein_target_g} g`}
          color="#10B981"
        />
      </div>

      {/* Extra kcal + multipliers */}
      <div className="flex items-center gap-2 mt-3 flex-wrap text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-700 dark:text-sky-300 font-semibold">
          +{data.extra_kcal_per_day} kcal/{bg ? "ден" : "day"}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-700 dark:text-purple-300 font-semibold">
          {bg ? "Stage мод." : "Stage mod."} ×{data.stage_modifier}
        </span>
        {data.combined_condition_modifier !== 1.0 && (
          <span className="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-700 dark:text-rose-300 font-semibold">
            {bg ? "Conditions" : "Conditions"} ×{data.combined_condition_modifier}
          </span>
        )}
      </div>

      {/* Conditions chips */}
      {conditions.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-bold opacity-70 mb-1 uppercase tracking-wider">
            {bg ? "Приложени състояния" : "Applied conditions"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {conditions.map((c) => (
              <span
                key={c.key}
                title={c.rationale_bg || ""}
                className={`text-[11px] px-2 py-1 rounded-full font-medium border ${
                  isDark
                    ? "bg-black/30 border-pink-500/30 text-pink-200"
                    : "bg-white/60 border-pink-200 text-pink-800"
                }`}
                data-testid={`perinatal-condition-${c.key}`}
              >
                {bg ? c.label_bg : c.label_en} <b>×{c.factor}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Expand for rationale */}
      {!compact && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-xs font-semibold text-pink-600 dark:text-pink-300 hover:opacity-80 inline-flex items-center gap-1"
          data-testid="perinatal-expand"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded
            ? (bg ? "Скрий подробности" : "Hide details")
            : (bg ? "Защо тази стойност?" : "Why this value?")}
        </button>
      )}

      {expanded && (
        <div className="mt-2 space-y-2 text-[11px] leading-relaxed">
          <div className={`p-2.5 rounded-lg ${isDark ? "bg-black/30" : "bg-white/60"}`}>
            <div className="font-bold mb-0.5 flex items-center gap-1">
              <Activity className="w-3 h-3" />
              {bg ? "Stage rationale" : "Stage rationale"}
            </div>
            <span className="opacity-80">{data.rationale_bg || data.label_en}</span>
          </div>
          {conditions.map((c) => (
            <div key={`r-${c.key}`} className={`p-2.5 rounded-lg ${isDark ? "bg-black/30" : "bg-white/60"}`}>
              <div className="font-bold mb-0.5">
                {bg ? c.label_bg : c.label_en} <span className="opacity-50">×{c.factor}</span>
              </div>
              <span className="opacity-80">{c.rationale_bg}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] opacity-50 italic mt-3 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        <span>{data.disclaimer}</span>
      </p>
    </motion.div>
  );
}

function CeilingStat({ label, value, color, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-[10px] opacity-70 uppercase tracking-wider">{label}</div>
      <div className="text-xl sm:text-2xl font-extrabold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
